/**
 * useBannerManagement Hook
 * Extracted banner management logic from AppSettings.tsx
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    fetchBanners,
    createBanner,
    updateBanner,
    deleteBanner,
    type Banner,
    type CreateBannerPayload,
    type UpdateBannerPayload,
    type MediaInputItem,
} from '../../../api/admin';
import { readFileAsDataUrl } from '../utils';
import type { BannerEditState, MediaEditItem } from '../types';
import { logger } from '../../../utils/logger';

interface UseBannerManagementReturn {
    banners: BannerEditState[];
    setBanners: React.Dispatch<React.SetStateAction<BannerEditState[]>>;
    originalBanners: Banner[];
    isLoadingBanners: boolean;
    bannersChanged: boolean;
    setBannersChanged: React.Dispatch<React.SetStateAction<boolean>>;

    // YouTube modal state
    youtubeModalOpen: boolean;
    setYoutubeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    youtubeModalBannerIndex: number | null;
    setYoutubeModalBannerIndex: React.Dispatch<React.SetStateAction<number | null>>;
    youtubeUrlInput: string;
    setYoutubeUrlInput: React.Dispatch<React.SetStateAction<string>>;

    // Banner handlers
    handleAddBannerFiles: (fileList?: FileList | File[]) => Promise<void>;
    handleBannerTextChange: (index: number, key: 'title' | 'body' | 'linkUrl', value: string) => void;
    handleBannerRemove: (index: number) => void;
    handleAddImagesToBanner: (bannerIndex: number, fileList?: FileList | File[]) => Promise<void>;
    handleRemoveImageFromBanner: (bannerIndex: number, imageIndex: number) => void;
    handleReorderImagesInBanner: (bannerIndex: number, fromImageIndex: number, toImageIndex: number) => void;
    handleBannerReorder: (fromIndex: number, toIndex: number) => void;
    extractYouTubeVideoId: (url: string) => string | null;
    handleAddYouTubeVideo: (bannerIndex: number, youtubeUrl: string) => boolean;
    handleRemoveMediaFromBanner: (bannerIndex: number, mediaIndex: number) => void;
    handleReorderMediaInBanner: (bannerIndex: number, fromIndex: number, toIndex: number) => void;
    handleAddMediaToBanner: (bannerIndex: number, fileList?: FileList | File[]) => Promise<void>;
    handleActionLinkAdd: (bannerIndex: number) => void;
    handleActionLinkChange: (bannerIndex: number, linkIndex: number, field: 'image' | 'url', value: string) => void;
    handleActionLinkRemove: (bannerIndex: number, linkIndex: number) => void;
    handleActionLinkImageUpload: (bannerIndex: number, linkIndex: number, file: File) => Promise<void>;

    // Save banners
    saveBanners: () => Promise<void>;
    isSavingBanners: boolean;
}

export function useBannerManagement(): UseBannerManagementReturn {
    const [banners, setBanners] = useState<BannerEditState[]>([]);
    const [originalBanners, setOriginalBanners] = useState<Banner[]>([]);
    const [isLoadingBanners, setIsLoadingBanners] = useState(false);
    const [bannersChanged, setBannersChanged] = useState(false);
    const [isSavingBanners, setIsSavingBanners] = useState(false);

    // YouTube modal state
    const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
    const [youtubeModalBannerIndex, setYoutubeModalBannerIndex] = useState<number | null>(null);
    const [youtubeUrlInput, setYoutubeUrlInput] = useState('');

    // Load banners from API on mount
    useEffect(() => {
        const loadBanners = async () => {
            setIsLoadingBanners(true);
            try {
                const response = await fetchBanners({ includeInactive: true });
                const loadedBanners = response.banners.map((banner): BannerEditState => {
                    let mediaItems: MediaEditItem[] = [];

                    if (banner.media && banner.media.length > 0) {
                        mediaItems = banner.media.map((item): MediaEditItem => {
                            if (item.type === 'video') {
                                return {
                                    type: 'video',
                                    data: item.thumbnail || `https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`,
                                    youtubeId: item.youtubeId,
                                    isNew: false,
                                };
                            } else {
                                return {
                                    type: 'image',
                                    data: item.large || item.medium || item.thumbnail || '',
                                    isNew: false,
                                };
                            }
                        });
                    } else if (banner.images && banner.images.length > 0) {
                        mediaItems = banner.images.map((img): MediaEditItem => ({
                            type: 'image',
                            data: img.medium || img.large || img.thumbnail || '',
                            isNew: false,
                        }));
                    }

                    const legacyImages = banner.images?.map(img => img.medium || img.large || img.thumbnail).filter((url): url is string => !!url) || [];

                    return {
                        id: banner.id,
                        title: banner.title,
                        body: banner.body || '',
                        linkUrl: banner.linkUrl || '',
                        media: mediaItems,
                        images: legacyImages,
                        actionLinks: (banner.metadata?.action_links || []).map((link: { image: string | { medium?: string; thumbnail?: string }; url: string }, idx: number) => {
                            // Handle both string (base64/URL) and object (processed image) formats
                            const imageValue = typeof link.image === 'string'
                                ? link.image
                                : link.image?.medium || link.image?.thumbnail || '';
                            return {
                                id: `${banner.id}-action-${idx}`,
                                image: imageValue,
                                url: link.url || ''
                            };
                        })
                    };
                });
                setBanners(loadedBanners);
                setOriginalBanners(response.banners);
                setBannersChanged(false);
            } catch (error) {
                logger.error('Failed to load banners:', error);
                toast.error('ไม่สามารถโหลด banners ได้');
            } finally {
                setIsLoadingBanners(false);
            }
        };

        loadBanners();
    }, []);

    const handleAddBannerFiles = useCallback(async (fileList?: FileList | File[]) => {
        const files = Array.from(fileList ?? []);
        if (files.length === 0) return;

        const newBanners: BannerEditState[] = [];
        let rejected = false;

        for (const file of files) {
            const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
            if (!file.type.startsWith('image/') && !isSvg) {
                rejected = true;
                continue;
            }

            try {
                const dataUrl = await readFileAsDataUrl(file);
                newBanners.push({
                    title: '',
                    body: '',
                    linkUrl: '',
                    media: [{ type: 'image', data: dataUrl, isNew: true }],
                    images: [dataUrl],
                    actionLinks: [],
                });
            } catch (error) {
                logger.error('Failed to read banner file', error);
                rejected = true;
            }
        }

        if (rejected) {
            toast.error('บางไฟล์ไม่สามารถใช้งานได้ กรุณาเลือกไฟล์ภาพหรือ .svg เท่านั้น');
        }

        if (newBanners.length > 0) {
            setBanners(prev => [...prev, ...newBanners]);
            setBannersChanged(true);
        }
    }, []);

    const handleBannerTextChange = useCallback((index: number, key: 'title' | 'body' | 'linkUrl', value: string) => {
        setBanners(prev => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index] = { ...updated[index], [key]: value };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleBannerRemove = useCallback((index: number) => {
        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[index];

            if (banner?.id && !banner.id.startsWith('temp-')) {
                updated[index] = { ...banner, isDeleted: true };
            } else {
                updated.splice(index, 1);
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleAddImagesToBanner = useCallback(async (bannerIndex: number, fileList?: FileList | File[]) => {
        const files = Array.from(fileList ?? []);
        if (files.length === 0) return;

        const banner = banners[bannerIndex];
        if (!banner) return;

        const currentCount = banner.images.length;
        const remaining = 10 - currentCount;
        if (remaining <= 0) {
            toast.error('จำนวนรูปเต็มแล้ว (สูงสุด 10 รูป)');
            return;
        }

        const filesToProcess = files.slice(0, remaining);
        if (filesToProcess.length < files.length) {
            toast(`เลือกได้แค่ ${remaining} รูป (สูงสุด 10 รูป)`);
        }

        const newImages: string[] = [];
        let rejected = false;

        for (const file of filesToProcess) {
            const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
            if (!file.type.startsWith('image/') && !isSvg) {
                rejected = true;
                continue;
            }
            try {
                const dataUrl = await readFileAsDataUrl(file);
                newImages.push(dataUrl);
            } catch {
                rejected = true;
            }
        }

        if (rejected) {
            toast.error('บางไฟล์ไม่สามารถใช้งานได้');
        }

        if (newImages.length > 0) {
            setBanners(prev => {
                const updated = [...prev];
                if (updated[bannerIndex]) {
                    updated[bannerIndex] = {
                        ...updated[bannerIndex],
                        images: [...updated[bannerIndex].images, ...newImages],
                    };
                }
                return updated;
            });
            setBannersChanged(true);
        }
    }, [banners]);

    const handleRemoveImageFromBanner = useCallback((bannerIndex: number, imageIndex: number) => {
        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (banner && banner.images.length > imageIndex) {
                const newImages = [...banner.images];
                newImages.splice(imageIndex, 1);
                updated[bannerIndex] = { ...banner, images: newImages };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleReorderImagesInBanner = useCallback((bannerIndex: number, fromImageIndex: number, toImageIndex: number) => {
        if (fromImageIndex === toImageIndex) return;

        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (!banner) return prev;

            const images = [...banner.images];
            if (fromImageIndex < 0 || toImageIndex < 0 || fromImageIndex >= images.length || toImageIndex >= images.length) {
                return prev;
            }

            const [moved] = images.splice(fromImageIndex, 1);
            images.splice(toImageIndex, 0, moved);
            updated[bannerIndex] = { ...banner, images };
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleBannerReorder = useCallback((fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        setBanners(prev => {
            if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
                return prev;
            }

            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const extractYouTubeVideoId = useCallback((url: string): string | null => {
        const patterns = [
            /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
            /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
            /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
            /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
            /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
        ];
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) return match[1];
        }
        return null;
    }, []);

    const handleAddYouTubeVideo = useCallback((bannerIndex: number, youtubeUrl: string) => {
        const videoId = extractYouTubeVideoId(youtubeUrl);
        if (!videoId) {
            toast.error('รูปแบบ URL YouTube ไม่ถูกต้อง');
            return false;
        }

        const banner = banners[bannerIndex];
        if (!banner) return false;

        const currentCount = banner.media.length;
        if (currentCount >= 10) {
            toast.error('จำนวนสื่อเต็มแล้ว (สูงสุด 10 รายการ)');
            return false;
        }

        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        setBanners(prev => {
            const updated = [...prev];
            if (updated[bannerIndex]) {
                updated[bannerIndex] = {
                    ...updated[bannerIndex],
                    media: [
                        ...updated[bannerIndex].media,
                        { type: 'video', data: thumbnailUrl, youtubeId: videoId, isNew: true }
                    ],
                };
            }
            return updated;
        });
        setBannersChanged(true);
        return true;
    }, [banners, extractYouTubeVideoId]);

    const handleRemoveMediaFromBanner = useCallback((bannerIndex: number, mediaIndex: number) => {
        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (banner && banner.media.length > mediaIndex) {
                const newMedia = [...banner.media];
                const removedItem = newMedia.splice(mediaIndex, 1)[0];

                let newImages = [...banner.images];
                if (removedItem.type === 'image') {
                    const imgIndex = newImages.indexOf(removedItem.data);
                    if (imgIndex >= 0) {
                        newImages.splice(imgIndex, 1);
                    }
                }

                updated[bannerIndex] = { ...banner, media: newMedia, images: newImages };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleReorderMediaInBanner = useCallback((bannerIndex: number, fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (!banner) return prev;

            const media = [...banner.media];
            if (fromIndex < 0 || toIndex < 0 || fromIndex >= media.length || toIndex >= media.length) {
                return prev;
            }

            const [moved] = media.splice(fromIndex, 1);
            media.splice(toIndex, 0, moved);
            updated[bannerIndex] = { ...banner, media };
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleAddMediaToBanner = useCallback(async (bannerIndex: number, fileList?: FileList | File[]) => {
        const files = Array.from(fileList ?? []);
        if (files.length === 0) return;

        const banner = banners[bannerIndex];
        if (!banner) return;

        const currentCount = banner.media.length;
        const remaining = 10 - currentCount;
        if (remaining <= 0) {
            toast.error('จำนวนสื่อเต็มแล้ว (สูงสุด 10 รายการ)');
            return;
        }

        const filesToProcess = files.slice(0, remaining);
        if (filesToProcess.length < files.length) {
            toast(`เลือกได้แค่ ${remaining} รูป (สูงสุด 10 รายการ)`);
        }

        const newMediaItems: MediaEditItem[] = [];
        const newImages: string[] = [];
        let rejected = false;

        for (const file of filesToProcess) {
            const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
            if (!file.type.startsWith('image/') && !isSvg) {
                rejected = true;
                continue;
            }
            try {
                const dataUrl = await readFileAsDataUrl(file);
                newMediaItems.push({ type: 'image', data: dataUrl, isNew: true });
                newImages.push(dataUrl);
            } catch {
                rejected = true;
            }
        }

        if (rejected) {
            toast.error('บางไฟล์ไม่สามารถใช้งานได้');
        }

        if (newMediaItems.length > 0) {
            setBanners(prev => {
                const updated = [...prev];
                if (updated[bannerIndex]) {
                    updated[bannerIndex] = {
                        ...updated[bannerIndex],
                        media: [...updated[bannerIndex].media, ...newMediaItems],
                        images: [...updated[bannerIndex].images, ...newImages],
                    };
                }
                return updated;
            });
            setBannersChanged(true);
        }
    }, [banners]);

    const handleActionLinkAdd = useCallback((bannerIndex: number) => {
        setBanners(prev => {
            const updated = [...prev];
            if (updated[bannerIndex]) {
                updated[bannerIndex] = {
                    ...updated[bannerIndex],
                    actionLinks: [
                        ...updated[bannerIndex].actionLinks,
                        { image: '', url: '', isNew: true }
                    ],
                };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleActionLinkChange = useCallback((bannerIndex: number, linkIndex: number, field: 'image' | 'url', value: string) => {
        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (banner && banner.actionLinks[linkIndex]) {
                const newLinks = [...banner.actionLinks];
                newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: value };
                updated[bannerIndex] = { ...banner, actionLinks: newLinks };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleActionLinkRemove = useCallback((bannerIndex: number, linkIndex: number) => {
        setBanners(prev => {
            const updated = [...prev];
            const banner = updated[bannerIndex];
            if (banner && banner.actionLinks[linkIndex]) {
                const link = banner.actionLinks[linkIndex];
                const newLinks = [...banner.actionLinks];
                if (link.id && !link.isNew) {
                    newLinks[linkIndex] = { ...link, isDeleted: true };
                } else {
                    newLinks.splice(linkIndex, 1);
                }
                updated[bannerIndex] = { ...banner, actionLinks: newLinks };
            }
            return updated;
        });
        setBannersChanged(true);
    }, []);

    const handleActionLinkImageUpload = useCallback(async (bannerIndex: number, linkIndex: number, file: File) => {
        try {
            const dataUrl = await readFileAsDataUrl(file);
            handleActionLinkChange(bannerIndex, linkIndex, 'image', dataUrl);
        } catch {
            toast.error('ไม่สามารถอ่านไฟล์รูปภาพได้');
        }
    }, [handleActionLinkChange]);

    // Save banners to API
    const saveBanners = useCallback(async () => {
        setIsSavingBanners(true);
        try {
            const visibleBanners = banners.filter(b => !b.isDeleted);

            for (const banner of banners) {
                if (banner.isDeleted && banner.id && !banner.id.startsWith('temp-')) {
                    await deleteBanner(banner.id);
                } else if (!banner.id || banner.id.startsWith('temp-')) {
                    if (!banner.isDeleted) {
                        const mediaItems: MediaInputItem[] = banner.media
                            .filter(m => m.type === 'image' ? m.data : m.youtubeId)
                            .map(m => {
                                if (m.type === 'video') {
                                    return { type: 'video' as const, data: m.youtubeId! };
                                }
                                return { type: 'image' as const, data: m.data };
                            });

                        const payload: CreateBannerPayload = {
                            title: banner.title || 'Untitled Banner',
                            body: banner.body || null,
                            linkUrl: banner.linkUrl || null,
                            media: mediaItems,
                            sortOrder: visibleBanners.indexOf(banner),
                            action_links: banner.actionLinks
                                .filter(l => !l.isDeleted && l.image)
                                .map(l => ({ image_base64: l.image, url: l.url || '' })),
                        };
                        await createBanner(payload);
                    }
                } else {
                    const mediaItems: MediaInputItem[] = banner.media
                        .filter(m => m.type === 'image' ? m.data : m.youtubeId)
                        .map(m => {
                            if (m.type === 'video') {
                                return { type: 'video' as const, data: m.youtubeId! };
                            }
                            return { type: 'image' as const, data: m.data };
                        });

                    const payload: UpdateBannerPayload = {
                        title: banner.title || undefined,
                        body: banner.body || null,
                        linkUrl: banner.linkUrl || null,
                        media: mediaItems,
                        sortOrder: visibleBanners.indexOf(banner),
                        action_links: banner.actionLinks
                            .filter(l => !l.isDeleted && l.image)
                            .map(l => ({ image_base64: l.image, url: l.url || '' })),
                    };
                    await updateBanner(banner.id, payload);
                }
            }

            // Reload banners
            const response = await fetchBanners({ includeInactive: true });
            const loadedBanners = response.banners.map((banner): BannerEditState => {
                let mediaItems: MediaEditItem[] = [];
                if (banner.media && banner.media.length > 0) {
                    mediaItems = banner.media.map((item): MediaEditItem => {
                        if (item.type === 'video') {
                            return {
                                type: 'video',
                                data: item.thumbnail || `https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`,
                                youtubeId: item.youtubeId,
                                isNew: false,
                            };
                        }
                        return {
                            type: 'image',
                            data: item.large || item.medium || item.thumbnail || '',
                            isNew: false,
                        };
                    });
                } else if (banner.images && banner.images.length > 0) {
                    mediaItems = banner.images.map((img): MediaEditItem => ({
                        type: 'image',
                        data: img.medium || img.large || img.thumbnail || '',
                        isNew: false,
                    }));
                }

                const legacyImages = banner.images?.map(img => img.medium || img.large || img.thumbnail).filter((url): url is string => !!url) || [];

                return {
                    id: banner.id,
                    title: banner.title,
                    body: banner.body || '',
                    linkUrl: banner.linkUrl || '',
                    media: mediaItems,
                    images: legacyImages,
                    actionLinks: (banner.metadata?.action_links || []).map((link: { image: string | { medium?: string; thumbnail?: string }; url: string }, idx: number) => {
                        // Handle both string (base64/URL) and object (processed image) formats
                        const imageValue = typeof link.image === 'string'
                            ? link.image
                            : link.image?.medium || link.image?.thumbnail || '';
                        return {
                            id: `${banner.id}-action-${idx}`,
                            image: imageValue,
                            url: link.url || ''
                        };
                    })
                };
            });

            setBanners(loadedBanners);
            setOriginalBanners(response.banners);
            setBannersChanged(false);
            toast.success('บันทึก banners เรียบร้อยแล้ว');
        } catch (error) {
            logger.error('Failed to save banners:', error);
            toast.error('ไม่สามารถบันทึก banners ได้');
        } finally {
            setIsSavingBanners(false);
        }
    }, [banners]);

    return {
        banners,
        setBanners,
        originalBanners,
        isLoadingBanners,
        bannersChanged,
        setBannersChanged,
        youtubeModalOpen,
        setYoutubeModalOpen,
        youtubeModalBannerIndex,
        setYoutubeModalBannerIndex,
        youtubeUrlInput,
        setYoutubeUrlInput,
        handleAddBannerFiles,
        handleBannerTextChange,
        handleBannerRemove,
        handleAddImagesToBanner,
        handleRemoveImageFromBanner,
        handleReorderImagesInBanner,
        handleBannerReorder,
        extractYouTubeVideoId,
        handleAddYouTubeVideo,
        handleRemoveMediaFromBanner,
        handleReorderMediaInBanner,
        handleAddMediaToBanner,
        handleActionLinkAdd,
        handleActionLinkChange,
        handleActionLinkRemove,
        handleActionLinkImageUpload,
        saveBanners,
        isSavingBanners,
    };
}
